import Cookies from "js-cookie";
import ReconnectingWebSocket from "reconnecting-websocket";
import { BehaviorSubject, Subject, fromEvent } from "rxjs";
import { filter, share, shareReplay, takeUntil } from "rxjs/operators";
import apiFactory from ".";

const REACT_APP_AUTHENTICATION_WEB =
  process.env.REACT_APP_AUTHENTICATION_WEB || "http://localhost:4005";

const MAX_RETRIES_LIMIT = 5;
const SOCKET_STATUS = {
  CONNECTING: "CONNECTING",
  CONNECTED: "CONNECTED",
  DISCONNECTED: "DISCONNECTED",
};

const hardLogout = () => {
  Cookies.remove("access_token");
  Cookies.remove("refresh_token");
  localStorage.clear();
  window.location.href = REACT_APP_AUTHENTICATION_WEB + "/login";
};

// ========= refresh token lock =========
let refreshingTokenPromise = null;
let lastRefreshAt = 0;
const REFRESH_COOLDOWN_MS = 3000;

const refreshAccessToken = async () => {
  const now = Date.now();
  if (refreshingTokenPromise && now - lastRefreshAt < REFRESH_COOLDOWN_MS) {
    return refreshingTokenPromise;
  }

  if (!refreshingTokenPromise) {
    lastRefreshAt = now;
    refreshingTokenPromise = (async () => {
      try {
        const result = await apiFactory.authApi.getAccessToken({
          refreshToken: Cookies.get("refresh_token"),
        });

        if (result?.status === 400) {
          hardLogout();
          return null;
        }

        if (result?.status === 200) {
          Cookies.set("access_token", result.data);
          return result;
        }

        // hardLogout();
        return null;
      } catch (e) {
        return null;
      } finally {
        refreshingTokenPromise = null;
      }
    })();
  }

  return refreshingTokenPromise;
};

// ========= internal state =========
let chatSocket = null;
let authSocket = null;
let chatSocketId = 0;
let authSocketId = 0;

let connectedSuccess = 0;

const chatStop$ = new Subject(); // hủy stream cũ khi recreate socket
const authStop$ = new Subject();

const socketStatusSubject = new BehaviorSubject({
  scope: "chat", // 'chat' | 'auth'
  status: SOCKET_STATUS.DISCONNECTED,
  retryCount: 0,
  connectedSuccess,
});

const socketStatus$ = socketStatusSubject.asObservable().pipe(shareReplay(1));

// ========= events subjects =========
const chatEventSubject = new Subject(); // push parsed JSON vào đây
const authEventSubject = new Subject();

const chatEvents$ = chatEventSubject.asObservable().pipe(share());
const authEvents$ = authEventSubject.asObservable().pipe(share());

// ========= helpers =========
const buildChatSocketUrl = () => {
  const url = process.env.REACT_APP_WEBSOCKET_URL;
  const access_token = Cookies.get("access_token");
  if (!access_token || !url || access_token?.includes("https://")) return null;
  return `${url}/websocket?Bearer=${access_token}`;
};

const buildAuthSocketUrl = () => {
  const url = process.env.REACT_APP_AUTH_WEBSOCKET_URL;
  const access_token = Cookies.get("access_token");
  if (!access_token || !url || access_token?.includes("https://")) return null;
  return `${url}/websocket?Bearer=${access_token}`;
};

const safeJsonParse = (raw) => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// ========= init / close =========
const initChatSocket = () => {
  // nếu socket còn sống thì không init lại
  if (chatSocket && chatSocket.readyState !== WebSocket.CLOSED) return;

  closeChatSocket();

  if (!("WebSocket" in window)) return;

  const socketUrl = buildChatSocketUrl();
  if (!socketUrl) return;

  const myId = ++chatSocketId;
  let retryCount = 0;

  socketStatusSubject.next({
    scope: "chat",
    status: SOCKET_STATUS.CONNECTING,
    retryCount,
    connectedSuccess,
  });

  chatSocket = new ReconnectingWebSocket(socketUrl, [], {
    maxRetries: 20,
    reconnectInterval: 3000,
    reconnectDecay: 1.5,
  });

  fromEvent(chatSocket, "message")
    .pipe(takeUntil(chatStop$))
    .subscribe((event) => {
      if (myId !== chatSocketId) return;

      const raw = event?.data;

      // ping/pong
      if (typeof raw === "string" && raw === "PING") {
        try {
          chatSocket?.send("PONG");
        } catch {}
        return;
      }

      if (typeof raw !== "string") return;

      const parsed = safeJsonParse(raw);
      if (!parsed) return;

      chatEventSubject.next(parsed);
    });

  fromEvent(chatSocket, "open")
    .pipe(takeUntil(chatStop$))
    .subscribe(() => {
      if (myId !== chatSocketId) return;

      retryCount = 0;
      connectedSuccess += 1;
      socketStatusSubject.next({
        scope: "chat",
        status: SOCKET_STATUS.CONNECTED,
        retryCount,
        connectedSuccess,
      });
    });

  fromEvent(chatSocket, "error")
    .pipe(takeUntil(chatStop$))
    .subscribe(async () => {
      if (myId !== chatSocketId) return;

      retryCount += 1;
      socketStatusSubject.next({
        scope: "chat",
        status: SOCKET_STATUS.DISCONNECTED,
        retryCount,
        connectedSuccess,
      });

      // Không refresh token ở đây để tránh spam.
      // Refresh nên làm ở close code auth hoặc app chủ động khi token hết hạn.
    });

  fromEvent(chatSocket, "close")
    .pipe(takeUntil(chatStop$))
    .subscribe(async (event) => {
      if (myId !== chatSocketId) return;

      socketStatusSubject.next({
        scope: "chat",
        status: SOCKET_STATUS.DISCONNECTED,
        retryCount,
        connectedSuccess,
      });

      const code = event?.code;

      // các code này tuỳ backend; thêm bớt theo hệ thống bạn
      const maybeAuthClose =
        code === 1008 ||
        code === 4001 ||
        code === 4401 ||
        code === 1011 ||
        code === 1006;

      if (maybeAuthClose) {
        const res = await refreshAccessToken();
        if (res?.data) {
          // recreate socket với token mới
          closeChatSocket();
          initChatSocket();
        } else {
          // hardLogout();
        }
      }
    });
};

const closeChatSocket = () => {
  if (chatSocket) {
    try {
      chatSocket.close(1000, "Normal closure: Closing chat WebSocket");
    } catch {}
    chatSocket = null;
  }
  chatStop$.next(true);
};

const initAuthSocket = () => {
  if (authSocket && authSocket.readyState !== WebSocket.CLOSED) return;

  closeAuthSocket();
  if (!("WebSocket" in window)) return;

  const socketUrl = buildAuthSocketUrl();
  if (!socketUrl) return;

  const myId = ++authSocketId;

  authSocket = new ReconnectingWebSocket(socketUrl, [], {
    maxRetries: 5,
    reconnectInterval: 3000,
    reconnectDecay: 2,
  });

  fromEvent(authSocket, "open")
    .pipe(takeUntil(authStop$))
    .subscribe(() => {
      if (myId !== authSocketId) return;
    });

  fromEvent(authSocket, "message")
    .pipe(takeUntil(authStop$))
    .subscribe((event) => {
      if (myId !== authSocketId) return;

      const raw = event?.data;

      if (typeof raw === "string" && raw === "PING") {
        try {
          authSocket?.send("PONG");
        } catch {}
        return;
      }

      if (typeof raw !== "string") return;

      const parsed = safeJsonParse(raw);
      if (parsed) authEventSubject.next(parsed);
    });

  fromEvent(authSocket, "close")
    .pipe(takeUntil(authStop$))
    .subscribe(async (event) => {
      if (myId !== authSocketId) return;

      const code = event?.code;
      const maybeAuthClose =
        code === 1008 || code === 4001 || code === 4401 || code === 1011;

      if (maybeAuthClose) {
        const res = await refreshAccessToken();
        if (res?.data) {
          closeAuthSocket();
          initAuthSocket();
        } else {
          // hardLogout();
        }
      }
    });

  fromEvent(authSocket, "error")
    .pipe(takeUntil(authStop$))
    .subscribe(async () => {
      // auth socket error -> thử refresh token 1 lần
      const res = await refreshAccessToken();
      if (res?.data) {
        closeAuthSocket();
        initAuthSocket();
      } else {
        // hardLogout();
      }
    });
};

const closeAuthSocket = () => {
  if (authSocket) {
    try {
      authSocket.close(1000, "Normal closure: Closing auth WebSocket");
    } catch {}
    authSocket = null;
  }
  authStop$.next(true);
};

const sendChat = (payload) => {
  if (!chatSocket || chatSocket.readyState !== WebSocket.OPEN) return false;
  try {
    chatSocket.send(
      typeof payload === "string" ? payload : JSON.stringify(payload)
    );
    return true;
  } catch {
    return false;
  }
};

const sendAuth = (payload) => {
  if (!authSocket || authSocket.readyState !== WebSocket.OPEN) return false;
  try {
    authSocket.send(
      typeof payload === "string" ? payload : JSON.stringify(payload)
    );
    return true;
  } catch {
    return false;
  }
};

// ========= optional operators (lọc tiện) =========
const chatEventsByType = (type) =>
  chatEvents$.pipe(filter((e) => e?.type === type));

const chatEventsByEvent = (eventName) =>
  chatEvents$.pipe(filter((e) => e?.event === eventName));

export {
  MAX_RETRIES_LIMIT,
  SOCKET_STATUS,
  authEvents$,
  chatEvents$,
  closeAuthSocket,
  closeChatSocket,
  initAuthSocket,
  initChatSocket,
  sendChat,
  socketStatus$,
};
